import React from "react"
import {
  BuildingOfficeIcon,
  ChartBarIcon,
  HeartIcon,
  ClockIcon,
  PauseCircleIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
  CalendarDaysIcon,
  StarIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline"

const PerformanceStatusOverview = ({ showDetails, toggleDetails }) => {
  return (
    <div className='mb-8'>
      <div className='bg-white rounded-lg shadow mb-6'>
        <button
          onClick={toggleDetails}
          className='w-full px-6 py-4 flex items-center justify-between text-left'
        >
          <div className='flex items-center'>
            <DocumentDuplicateIcon className='w-6 h-6 mr-3 text-blue-600' />
            <div>
              <h4 className='text-lg font-semibold text-gray-900'>
                Performance Status Overview
              </h4>
              <p className='text-sm text-gray-600'>
                Upcoming community partnership activities
              </p>
            </div>
          </div>
          <div className='flex items-center space-x-2'>
            {showDetails ? (
              <ChevronUpIcon className='w-5 h-5' />
            ) : (
              <ChevronDownIcon className='w-5 h-5' />
            )}
          </div>
        </button>

        {showDetails && (
          <div className='space-y-6 p-4 animate-in slide-in-from-top duration-300'>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-6'>
              <div className='bg-blue-50 p-4 rounded-lg shadow-sm'>
                <div className='flex items-center gap-2 text-blue-600'>
                  <BuildingOfficeIcon className='w-6 h-6' />
                  <span className='text-2xl font-bold'>1</span>
                </div>
                <p className='text-sm font-medium mt-1'>
                  New Institutions This Month
                </p>
                <p className='text-xs text-gray-500'>September 2025</p>
              </div>

              <div className='bg-purple-50 p-4 rounded-lg shadow-sm'>
                <div className='flex items-center gap-2 text-purple-600'>
                  <ChartBarIcon className='w-6 h-6' />
                  <span className='text-2xl font-bold'>0</span>
                </div>
                <p className='text-sm font-medium mt-1'>
                  New Partnerships This Month
                </p>
                <p className='text-xs text-gray-500'>September 2025</p>
              </div>

              <div className='bg-teal-50 p-4 rounded-lg shadow-sm'>
                <div className='flex items-center gap-2 text-teal-600'>
                  <HeartIcon className='w-6 h-6' />
                  <span className='text-2xl font-bold'>7</span>
                </div>
                <p className='text-sm font-medium mt-1'>Total Partnerships</p>
                <p className='text-xs text-gray-500'>
                  Active and inactive partnerships
                </p>
              </div>

              <div className='bg-green-50 p-4 rounded-lg shadow-sm'>
                <div className='flex items-center gap-2 text-green-600'>
                  <CheckCircleIcon className='w-6 h-6' />
                  <span className='text-2xl font-bold'>7</span>
                </div>
                <p className='text-sm font-medium mt-1'>
                  Total Active Partnerships
                </p>
                <p className='text-xs text-gray-500'>
                  Current active partnerships
                </p>
              </div>

              <div className='bg-orange-50 p-4 rounded-lg shadow-sm'>
                <div className='flex items-center gap-2 text-orange-600'>
                  <ClockIcon className='w-6 h-6' />
                  <span className='text-2xl font-bold'>0</span>
                </div>
                <p className='text-sm font-medium mt-1'>Pending Partnerships</p>
                <p className='text-xs text-gray-500'>Awaiting approval</p>
              </div>

              <div className='bg-red-50 p-4 rounded-lg shadow-sm'>
                <div className='flex items-center gap-2 text-red-600'>
                  <PauseCircleIcon className='w-6 h-6' />
                  <span className='text-2xl font-bold'>1</span>
                </div>
                <p className='text-sm font-medium mt-1'>
                  Inactive Partnerships
                </p>
                <p className='text-xs text-gray-500'>
                  Not engaged in ongoing activities
                </p>
              </div>
            </div>

            <div className='p-6'>
              <h3 className='font-semibold text-gray-800 mb-3'>
                Programs Initiated
              </h3>
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
                <div className='bg-blue-50 p-4 rounded-lg shadow-sm flex flex-col justify-between'>
                  <div>
                    <h4 className='text-base font-semibold text-gray-800'>
                      Total Ongoing Programs
                    </h4>
                    <p className='text-sm text-gray-600'>
                      Current ongoing this month
                    </p>
                  </div>
                  <p className='text-2xl font-bold text-blue-600 self-end'>0</p>
                </div>

                <div className='bg-teal-50 p-4 rounded-lg shadow-sm flex flex-col justify-between'>
                  <div>
                    <h4 className='text-base font-semibold text-gray-800'>
                      Upcoming Programs Next Month
                    </h4>
                    <p className='text-sm text-gray-600'>October 2025</p>
                  </div>
                  <p className='text-2xl font-bold text-teal-600 self-end'>0</p>
                </div>

                <div className='bg-green-50 p-4 rounded-lg shadow-sm flex flex-col justify-between'>
                  <div>
                    <h4 className='text-base font-semibold text-gray-800'>
                      Programs to Date
                    </h4>
                    <p className='text-sm text-gray-600'>
                      Total programs since inception
                    </p>
                  </div>
                  <p className='text-2xl font-bold text-green-600 self-end'>
                    7
                  </p>
                </div>
              </div>
            </div>

            <div className='p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 border-t'>
              <div className='p-4 border border-gray-200 bg-white rounded-xl shadow-sm'>
                <h3 className='font-semibold text-gray-800 mb-4'>
                  Community Feedback Breakdown
                </h3>
                <div className='space-y-4'>
                  <div>
                    <div className='flex items-center justify-between mb-1'>
                      <div className='flex items-center text-yellow-500'>
                        <span className='flex'>
                          {"★★★★★".split("").map((s, i) => (
                            <svg
                              key={i}
                              className='w-4 h-4 fill-yellow-500'
                              viewBox='0 0 20 20'
                            >
                              <path d='M10 15l-5.878 3.09 1.122-6.545L.488 6.91l6.561-.955L10 0l2.951 5.955 6.561.955-4.756 4.635 1.122 6.545z' />
                            </svg>
                          ))}
                        </span>
                        <span className='ml-2 text-gray-700'>(Excellent)</span>
                      </div>
                      <span className='text-yellow-600 font-medium'>
                        50 (42.0%)
                      </span>
                    </div>
                    <div className='w-full bg-gray-200 rounded-full h-2'>
                      <div
                        className='bg-yellow-500 h-2 rounded-full'
                        style={{ width: "42%" }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className='flex items-center justify-between mb-1'>
                      <div className='flex items-center text-yellow-500'>
                        <span className='flex'>
                          {"★★★★".split("").map((s, i) => (
                            <svg
                              key={i}
                              className='w-4 h-4 fill-yellow-500'
                              viewBox='0 0 20 20'
                            >
                              <path d='M10 15l-5.878 3.09 1.122-6.545L.488 6.91l6.561-.955L10 0l2.951 5.955 6.561.955-4.756 4.635 1.122 6.545z' />
                            </svg>
                          ))}
                        </span>
                        <span className='ml-2 text-gray-700'>(Good)</span>
                      </div>
                      <span className='text-yellow-600 font-medium'>
                        40 (33.0%)
                      </span>
                    </div>
                    <div className='w-full bg-gray-200 rounded-full h-2'>
                      <div
                        className='bg-yellow-500 h-2 rounded-full'
                        style={{ width: "33%" }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className='flex items-center justify-between mb-1'>
                      <div className='flex items-center text-yellow-500'>
                        <span className='flex'>
                          {"★★★".split("").map((s, i) => (
                            <svg
                              key={i}
                              className='w-4 h-4 fill-yellow-500'
                              viewBox='0 0 20 20'
                            >
                              <path d='M10 15l-5.878 3.09 1.122-6.545L.488 6.91l6.561-.955L10 0l2.951 5.955 6.561.955-4.756 4.635 1.122 6.545z' />
                            </svg>
                          ))}
                        </span>
                        <span className='ml-2 text-gray-700'>(Average)</span>
                      </div>
                      <span className='text-yellow-600 font-medium'>
                        18 (15.0%)
                      </span>
                    </div>
                    <div className='w-full bg-gray-200 rounded-full h-2'>
                      <div
                        className='bg-yellow-500 h-2 rounded-full'
                        style={{ width: "15%" }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className='flex items-center justify-between mb-1'>
                      <div className='flex items-center text-yellow-500'>
                        <span className='flex'>
                          {"★★".split("").map((s, i) => (
                            <svg
                              key={i}
                              className='w-4 h-4 fill-yellow-500'
                              viewBox='0 0 20 20'
                            >
                              <path d='M10 15l-5.878 3.09 1.122-6.545L.488 6.91l6.561-.955L10 0l2.951 5.955 6.561.955-4.756 4.635 1.122 6.545z' />
                            </svg>
                          ))}
                        </span>
                        <span className='ml-2 text-gray-700'>(Poor)</span>
                      </div>
                      <span className='text-yellow-600 font-medium'>
                        8 (7.0%)
                      </span>
                    </div>
                    <div className='w-full bg-gray-200 rounded-full h-2'>
                      <div
                        className='bg-yellow-500 h-2 rounded-full'
                        style={{ width: "7%" }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className='flex items-center justify-between mb-1'>
                      <div className='flex items-center text-yellow-500'>
                        <span className='flex'>
                          {"★".split("").map((s, i) => (
                            <svg
                              key={i}
                              className='w-4 h-4 fill-yellow-500'
                              viewBox='0 0 20 20'
                            >
                              <path d='M10 15l-5.878 3.09 1.122-6.545L.488 6.91l6.561-.955L10 0l2.951 5.955 6.561.955-4.756 4.635 1.122 6.545z' />
                            </svg>
                          ))}
                        </span>
                        <span className='ml-2 text-gray-700'>(Very Poor)</span>
                      </div>
                      <span className='text-yellow-600 font-medium'>
                        4 (3.0%)
                      </span>
                    </div>
                    <div className='w-full bg-gray-200 rounded-full h-2'>
                      <div
                        className='bg-yellow-500 h-2 rounded-full'
                        style={{ width: "3%" }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              {/* </div> */}

              <div className='p-4 border border-gray-200 bg-white rounded-xl shadow-sm'>
                <h2 className='text-lg font-semibold mb-4'>
                  Community Feedback Summary
                </h2>

                <div className='bg-orange-50 rounded-lg p-4 mb-3'>
                  <h3 className='text-base font-medium text-gray-800'>
                    Average Rating
                  </h3>
                  <p className='text-sm text-gray-500 mb-2'>
                    Average Program Rating
                  </p>
                  <p className='text-2xl font-bold text-orange-500'>
                    4.2{" "}
                    <span className='text-lg font-medium text-gray-600'>
                      / 5
                    </span>
                  </p>
                </div>

                <div className='bg-purple-50 rounded-lg p-4'>
                  <h3 className='text-base font-medium text-gray-800'>
                    Total Respondents
                  </h3>
                  <p className='text-sm text-gray-500 mb-2'>Respondent Count</p>
                  <p className='text-2xl font-bold text-purple-600'>
                    120
                    <span className='text-sm font-normal text-gray-600 ml-1'>
                      respondents
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PerformanceStatusOverview
